import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogContext";
import { Plus, Package } from "@phosphor-icons/react";

const statusStyle = {
  assigned: "bg-[#E0F2FE] text-[#075985] border-[#0EA5E9]",
  available: "bg-[#F0FDF4] text-[#166534] border-[#22C55E]",
  returned: "bg-slate-100 text-slate-700 border-slate-300",
  retired: "bg-red-50 text-red-700 border-red-300",
};

export default function Assets() {
  const { user } = useAuth();
  const { confirm } = useDialog();
  const canManage = user.role === "admin" || user.role === "hr";
  const [assets, setAssets] = useState([]);
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", asset_type: "Laptop", serial: "", assigned_to: "", notes: "" });
  const [error, setError] = useState("");

  const load = async () => {
    const r = await api.get("/assets");
    setAssets(r.data);
    if (canManage) { try { const u = await api.get("/users"); setUsers(u.data); } catch {} }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault(); setError("");
    try {
      await api.post("/assets", { ...form, assigned_to: form.assigned_to || null });
      setShowCreate(false);
      setForm({ name: "", asset_type: "Laptop", serial: "", assigned_to: "", notes: "" });
      load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete asset?",
      description: "This will permanently remove the asset record.",
      confirmText: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    await api.delete(`/assets/${id}`); load();
  };

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">Operations / Assets</div>
          <h1 className="font-display text-4xl font-black tracking-tight">Asset management</h1>
          <p className="text-sm text-muted-foreground mt-2">Track hardware and equipment assigned to team members.</p>
        </div>
        {canManage && (
          <button className="btn-primary flex items-center gap-2" onClick={() => setShowCreate(true)} data-testid="create-asset-button">
            <Plus size={16} weight="bold" /> New asset
          </button>
        )}
      </div>
      <div className="p-8 lg:p-12">
        <div className="border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted">
                <th className="text-left px-4 py-3 eyebrow">Asset</th>
                <th className="text-left px-4 py-3 eyebrow">Type</th>
                <th className="text-left px-4 py-3 eyebrow">Serial</th>
                <th className="text-left px-4 py-3 eyebrow">Assigned to</th>
                <th className="text-left px-4 py-3 eyebrow">Status</th>
                {canManage && <th className="px-4 py-3"></th>}
              </tr>
            </thead>
            <tbody>
              {assets.map(a => (
                <tr key={a.id} className="border-b border-border hover:bg-muted/50" data-testid={`asset-row-${a.id}`}>
                  <td className="px-4 py-3 flex items-center gap-2 font-medium"><Package size={16} weight="bold" />{a.name}</td>
                  <td className="px-4 py-3">{a.asset_type}</td>
                  <td className="px-4 py-3 font-mono-mini text-xs">{a.serial || "—"}</td>
                  <td className="px-4 py-3">{a.assignee_name || <span className="text-muted-foreground">Unassigned</span>}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] uppercase font-mono-mini border px-2 py-0.5 ${statusStyle[a.status] || ""}`}>{a.status}</span></td>
                  {canManage && (
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => remove(a.id)} className="text-xs text-muted-foreground hover:text-[#FF3B30]" data-testid={`delete-asset-${a.id}`}>Delete</button>
                    </td>
                  )}
                </tr>
              ))}
              {assets.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No assets yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white border border-border w-full max-w-md p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="create-asset-dialog">
            <div className="eyebrow mb-2">Add</div>
            <h2 className="font-display text-2xl font-bold mb-6">New asset</h2>
            <form onSubmit={create} className="space-y-4">
              <div><label className="eyebrow block mb-2">Name</label>
                <input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full border border-border px-3 py-2 text-sm" data-testid="asset-name-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="eyebrow block mb-2">Type</label>
                  <select value={form.asset_type} onChange={(e) => setForm({...form, asset_type: e.target.value})} className="w-full border border-border px-3 py-2 text-sm bg-white" data-testid="asset-type-select">
                    <option>Laptop</option><option>Phone</option><option>Monitor</option><option>Access Card</option><option>Headset</option><option>Other</option>
                  </select>
                </div>
                <div><label className="eyebrow block mb-2">Serial</label>
                  <input value={form.serial} onChange={(e) => setForm({...form, serial: e.target.value})} className="w-full border border-border px-3 py-2 text-sm" data-testid="asset-serial-input" /></div>
              </div>
              <div><label className="eyebrow block mb-2">Assign to</label>
                <select value={form.assigned_to} onChange={(e) => setForm({...form, assigned_to: e.target.value})} className="w-full border border-border px-3 py-2 text-sm bg-white" data-testid="asset-assignee-select">
                  <option value="">Unassigned</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                </select></div>
              <div><label className="eyebrow block mb-2">Notes</label>
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="w-full border border-border px-3 py-2 text-sm" /></div>
              {error && <div className="text-sm text-[#FF3B30] border border-[#FF3B30] px-3 py-2">{error}</div>}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 border border-border px-4 py-2 text-sm hover:bg-muted">Cancel</button>
                <button type="submit" className="flex-1 btn-primary" data-testid="submit-asset">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
